import { doc, getDoc, getDocs, collection, updateDoc, Timestamp, UpdateData } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Employee } from '../types/hr';

function convertTimestamp(value: unknown): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'object' && value !== null && 'seconds' in value) {
    return new Date((value as { seconds: number }).seconds * 1000);
  }
  try { return new Date(value as string); } catch { return undefined; }
}

function toEmployee(id: string, data: Record<string, unknown>): Employee {
  return {
    ...data,
    id,
    startDate: convertTimestamp(data.startDate),
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt),
  } as Employee;
}

export class EmployeeService {
  async getEmployee(employeeId: string): Promise<Employee | null> {
    const snap = await getDoc(doc(db, 'employees', employeeId));
    if (!snap.exists()) return null;
    return toEmployee(snap.id, snap.data() as Record<string, unknown>);
  }

  async getAllEmployees(): Promise<Employee[]> {
    const snap = await getDocs(collection(db, 'employees'));
    return snap.docs.map(d => toEmployee(d.id, d.data() as Record<string, unknown>));
  }

  async updateEmployee(employeeId: string, updates: Partial<Omit<Employee, 'id'>>): Promise<void> {
    await updateDoc(doc(db, 'employees', employeeId), updates as UpdateData<Employee>);
  }
}

export const employeeService = new EmployeeService();
